import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Pencil, Plus, Trash2, RefreshCw, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AddBotToGroupDialog } from './AddBotToGroupDialog';
import { GroupBotCard } from './GroupBotCard';
import { EditPriorityDialog } from './EditPriorityDialog';
import { EditGroupDialog } from './EditGroupDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface GroupCardProps {
  group: any;
  groupState?: { active_bot_id: string; active_since: string } | undefined;
  botStates?: Map<string, any>;
  winRateStats?: Map<string, { win_rate: number; total_forwarded_exits: number }>;
  onUpdate: () => void;
}

export const GroupCard: React.FC<GroupCardProps> = ({ 
  group, 
  groupState, 
  botStates, 
  winRateStats, 
  onUpdate 
}) => {
  const [isAddBotOpen, setIsAddBotOpen] = useState(false);
  const [isEditGroupOpen, setIsEditGroupOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<{
    id: string;
    bot_id: string;
    priority: number;
    group_id: string;
    bots?: { name: string };
  } | null>(null);
  const queryClient = useQueryClient();

  const members = group.strategy_group_members || [];
  
  // Memoize sorted members to avoid re-sorting on every render
  const sortedMembers = useMemo(() => 
    [...members].sort((a, b) => a.priority - b.priority),
    [members]
  );

  const toggleGroup = useMutation({
    mutationFn: async (enabled: boolean) => {
      // @ts-ignore - strategy_groups table will be created by migration
      const { error } = await supabase
        // @ts-ignore
        .from('strategy_groups')
        .update({ is_active: enabled, updated_at: new Date().toISOString() })
        .eq('id', group.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Group updated');
      onUpdate();
    },
    onError: (error) => {
      toast.error('Failed to update group');
      console.error(error);
    },
  });

  const recalculateActiveBot = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke(
        'conditional-skip',
        {
          body: { groupId: group.id }
        }
      );
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data?.selectedBot) {
        toast.success(
          `Active bot updated to: ${data.selectedBot.botName || 'Unknown'} (Priority ${data.selectedBot.priority})`
        );
      } else {
        toast.info('No bot in OPEN mode - active bot cleared');
      }
      onUpdate();
    },
    onError: (error) => {
      toast.error('Failed to recalculate active bot');
      console.error('Recalculation error:', error);
    },
  });

  const removeBotFromGroup = useMutation({
    mutationFn: async (memberId: string) => {
      // Step 1: Remove bot from group
      // @ts-ignore - strategy_group_members table
      const { error } = await supabase
        // @ts-ignore
        .from('strategy_group_members')
        .delete()
        .eq('id', memberId);
      
      if (error) throw error;
      
      // Step 2: Trigger recalculation after removal
      const { error: recalcError } = await supabase.functions.invoke(
        'conditional-skip',
        {
          body: { groupId: group.id }
        }
      );
      
      if (recalcError) {
        console.error('Failed to recalculate active bot:', recalcError);
        // Don't throw - bot was removed successfully
      }
    },
    onSuccess: () => {
      toast.success('Bot removed and group recalculated');
      onUpdate();
    },
    onError: (error) => {
      toast.error('Failed to remove bot');
      console.error(error);
    },
  });

  const deleteGroup = useMutation({
    mutationFn: async () => {
      // Delete group (cascade will handle members and states)
      // @ts-ignore - strategy_groups table
      const { error } = await supabase
        // @ts-ignore
        .from('strategy_groups')
        .delete()
        .eq('id', group.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Group deleted successfully');
      onUpdate();
    },
    onError: (error) => {
      toast.error('Failed to delete group');
      console.error(error);
    },
  });

  const handleEditPriority = (memberId: string, currentPriority: number) => {
    const member = members.find(m => m.id === memberId);
    if (member) {
      setEditingMember({
        id: member.id,
        bot_id: member.bot_id,
        priority: member.priority,
        group_id: group.id,
        bots: member.bots,
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CardTitle>{group.name}</CardTitle>
              <Badge variant="outline">{group.group_type}</Badge>
            </div>
            {group.description && (
              <p className="text-sm text-muted-foreground">{group.description}</p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => recalculateActiveBot.mutate()}
                disabled={!group.is_active || members.length === 0 || recalculateActiveBot.isPending}
                title="Recalculate active bot based on current states"
              >
                {recalculateActiveBot.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsEditGroupOpen(true)}
                title="Edit group"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsDeleteAlertOpen(true)}
                title="Delete group"
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Enabled</span>
              <Switch
                checked={group.is_active}
                onCheckedChange={(checked) => toggleGroup.mutate(checked)}
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Bot Cards Grid */}
        {sortedMembers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sortedMembers.map((member) => (
            <GroupBotCard
              key={member.id}
              member={member}
              isActiveInGroup={groupState?.active_bot_id === member.bot_id}
              currentState={botStates?.get(member.bot_id)}
              winRate={winRateStats?.get(member.bot_id)?.win_rate}
              totalExits={winRateStats?.get(member.bot_id)?.total_forwarded_exits}
              onEditPriority={handleEditPriority}
              onRemove={(memberId) => removeBotFromGroup.mutate(memberId)}
            />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No bots in this group yet
          </div>
        )}

        {/* Add Bot Button */}
        <div className="mt-4">
          <Button variant="outline" onClick={() => setIsAddBotOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Bot to Group
          </Button>
        </div>

        {/* Add Bot Dialog */}
        <AddBotToGroupDialog
          open={isAddBotOpen}
          onOpenChange={setIsAddBotOpen}
          groupId={group.id}
          existingPriorities={sortedMembers.map(m => m.priority)}
          onSuccess={onUpdate}
        />

        {/* Edit Priority Dialog */}
        <EditPriorityDialog
          open={!!editingMember}
          onOpenChange={(open) => !open && setEditingMember(null)}
          member={editingMember}
          existingPriorities={sortedMembers.map(m => m.priority)}
          onSuccess={onUpdate}
        />

        {/* Edit Group Dialog */}
        <EditGroupDialog
          open={isEditGroupOpen}
          onOpenChange={setIsEditGroupOpen}
          group={group}
          onSuccess={onUpdate}
        />

        {/* Delete Group Alert */}
        <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Strategy Group?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete "{group.name}" and remove all {members.length} bot assignment(s).
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteGroup.mutate()}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete Group
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};
